import { useEffect, lazy, Suspense } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { BookingProvider } from "@/contexts/BookingContext";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "@/components/ScrollToTop";
import VisitorTracker from "@/components/VisitorTracker";
import PageViewTracker from "@/components/PageViewTracker";
import ErrorBoundary from "@/components/ErrorBoundary";
import IdlePrefetcher from "@/components/IdlePrefetcher";
import NotFound from "./pages/NotFound";

// Minimal page-transition fallback to prevent blank flash
const PageLoader = () => (
  <div className="min-h-screen bg-background" aria-hidden="true" />
);

const CHUNK_RECOVERY_KEY = "lazy_chunk_recovery_reloaded";

// Retry wrapper for lazy imports — handles stale chunks after deployments/long idle
const lazyWithRetry = (importFn: () => Promise<any>) =>
  lazy(() =>
    importFn()
      .then((module) => {
        if (typeof window !== "undefined") {
          sessionStorage.removeItem(CHUNK_RECOVERY_KEY);
        }
        return module;
      })
      .catch(() => {
        // Wait briefly then retry once
        return new Promise<any>((resolve) => setTimeout(resolve, 1500)).then(() =>
          importFn().catch((err) => {
            console.error("Failed to load chunk after retry:", err);

            // Auto-recover once without requiring manual refresh
            if (typeof window !== "undefined") {
              const alreadyReloaded = sessionStorage.getItem(CHUNK_RECOVERY_KEY) === "1";
              if (!alreadyReloaded) {
                sessionStorage.setItem(CHUNK_RECOVERY_KEY, "1");
                window.location.reload();
              }
            }

            // Keep a safe fallback while reload happens
            return { default: PageLoader };
          })
        );
      })
  );

// Lazy load non-critical pages to reduce main bundle
const CarDetail = lazyWithRetry(() => import("./pages/CarDetail"));
const Checkout = lazyWithRetry(() => import("./pages/Checkout"));
const BookingConfirmation = lazyWithRetry(() => import("./pages/BookingConfirmation"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const AboutUs = lazyWithRetry(() => import("./pages/AboutUs"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const CustomerPortal = lazyWithRetry(() => import("./pages/CustomerPortal"));
const CarRentalTbilisi = lazyWithRetry(() => import("./pages/CarRentalTbilisi"));
const CarRental4x4Tbilisi = lazyWithRetry(() => import("./pages/CarRental4x4Tbilisi"));
const CarRental4x4Kutaisi = lazyWithRetry(() => import("./pages/CarRental4x4Kutaisi"));
const CarRentalTbilisiAirport = lazyWithRetry(() => import("./pages/CarRentalTbilisiAirport"));
const CarRentalKutaisi = lazyWithRetry(() => import("./pages/CarRentalKutaisi"));
const CarRentalKutaisiAirport = lazyWithRetry(() => import("./pages/CarRentalKutaisiAirport"));
const CarHireKutaisi = lazyWithRetry(() => import("./pages/CarHireKutaisi"));
const CarRentalBatumiAirport = lazyWithRetry(() => import("./pages/CarRentalBatumiAirport"));
const CarRentalBatumi = lazyWithRetry(() => import("./pages/CarRentalBatumi"));
const CarRentalGudauri = lazyWithRetry(() => import("./pages/CarRentalGudauri"));
const CarRentalTbilisiToYerevan = lazyWithRetry(() => import("./pages/CarRentalTbilisiToYerevan"));
const CarHireTbilisi = lazyWithRetry(() => import("./pages/CarHireTbilisi"));
const CarRental4x4Yerevan = lazyWithRetry(() => import("./pages/CarRental4x4Yerevan"));
const CarRentalYerevan = lazyWithRetry(() => import("./pages/CarRentalYerevan"));
const CarRentalYerevanAirport = lazyWithRetry(() => import("./pages/CarRentalYerevanAirport"));
const CarHireYerevan = lazyWithRetry(() => import("./pages/CarHireYerevan"));
const RentACarGyumri = lazyWithRetry(() => import("./pages/RentACarGyumri"));
const CarRentalIstanbul = lazyWithRetry(() => import("./pages/CarRentalIstanbul"));
const CarRental4x4Istanbul = lazyWithRetry(() => import("./pages/CarRental4x4Istanbul"));
const CarRentalIstanbulAirport = lazyWithRetry(() => import("./pages/CarRentalIstanbulAirport"));
const GeorgiaExpedition = lazyWithRetry(() => import("./pages/GeorgiaExpedition"));
const GeorgiaFamilyTrip = lazyWithRetry(() => import("./pages/GeorgiaFamilyTrip"));
const GeorgiaArmeniaExpedition = lazyWithRetry(() => import("./pages/GeorgiaArmeniaExpedition"));
const Trips = lazyWithRetry(() => import("./pages/Trips"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const CarAccessGate = lazyWithRetry(() => import("./pages/CarAccessGate"));
// Index (homepage) is lazy too so its ~15 KiB of code + JSON-LD schema
// doesn't ship in the bundle of city / 4x4 / car-hire landing pages.
const Index = lazyWithRetry(() => import("./pages/Index"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes - prevent refetching on every mount
      gcTime: 1000 * 60 * 30, // 30 minutes cache
      refetchOnWindowFocus: false, // Don't refetch when user tabs back
      refetchOnMount: "always", // Always refetch on component mount to prevent stale/empty data after long idle
      retry: 2, // Retry failed queries twice
    },
  },
});

// Keyed wrappers force full remount on slug change — fixes Google Translate caching stale text
const KeyedCarDetail = () => {
  const { slug } = useParams();
  return <Suspense fallback={<PageLoader />}><CarDetail key={slug} /></Suspense>;
};
const KeyedBlogPost = () => {
  const { slug } = useParams();
  return <Suspense fallback={<PageLoader />}><BlogPost key={slug} /></Suspense>;
};

const App = () => {
  // Global handler for unhandled promise rejections (e.g. from browser translation breaking async flows)
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled rejection:", event.reason);
      event.preventDefault();
    };
    const handleError = (event: ErrorEvent) => {
      // Prevent white screen from translation-caused errors
      console.error("Global error:", event.error);
      event.preventDefault();
    };
    window.addEventListener("unhandledrejection", handleRejection);
    window.addEventListener("error", handleError);
    return () => {
      window.removeEventListener("unhandledrejection", handleRejection);
      window.removeEventListener("error", handleError);
    };
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
      <QueryClientProvider client={queryClient}>
          <BookingProvider>
            <Sonner />
            <BrowserRouter>
            <ScrollToTop />
            <VisitorTracker />
            <PageViewTracker />
            <IdlePrefetcher />
            <Routes>
              <Route path="/" element={<Suspense fallback={<PageLoader />}><Index /></Suspense>} />
              <Route path="/best-car-rental-tbilisi-georgia" element={<Suspense fallback={<PageLoader />}><CarRentalTbilisi /></Suspense>} />
              <Route path="/4x4-car-rental-tbilisi-georgia" element={<Suspense fallback={<PageLoader />}><CarRental4x4Tbilisi /></Suspense>} />
              <Route path="/4x4-car-rental-kutaisi-georgia" element={<Suspense fallback={<PageLoader />}><CarRental4x4Kutaisi /></Suspense>} />
              <Route path="/car-rental-tbilisi-airport-georgia" element={<Suspense fallback={<PageLoader />}><CarRentalTbilisiAirport /></Suspense>} />
              <Route path="/rent-a-car-kutaisi-georgia" element={<Suspense fallback={<PageLoader />}><CarRentalKutaisi /></Suspense>} />
              <Route path="/car-rental-kutaisi-airport-georgia" element={<Suspense fallback={<PageLoader />}><CarRentalKutaisiAirport /></Suspense>} />
              <Route path="/car-hire-kutaisi-georgia" element={<Suspense fallback={<PageLoader />}><CarHireKutaisi /></Suspense>} />
              <Route path="/car-rental-batumi-airport-georgia" element={<Suspense fallback={<PageLoader />}><CarRentalBatumiAirport /></Suspense>} />
              <Route path="/car-rental-batumi-georgia" element={<Suspense fallback={<PageLoader />}><CarRentalBatumi /></Suspense>} />
              <Route path="/car-rental-gudauri-georgia" element={<Suspense fallback={<PageLoader />}><CarRentalGudauri /></Suspense>} />
              <Route path="/rent-car-tbilisi-to-yerevan" element={<Suspense fallback={<PageLoader />}><CarRentalTbilisiToYerevan /></Suspense>} />
              <Route path="/car-hire-tbilisi-georgia" element={<Suspense fallback={<PageLoader />}><CarHireTbilisi /></Suspense>} />
              <Route path="/4x4-rental-yerevan" element={<Suspense fallback={<PageLoader />}><CarRental4x4Yerevan /></Suspense>} />
              <Route path="/car-rental-yerevan-armenia" element={<Suspense fallback={<PageLoader />}><CarRentalYerevan /></Suspense>} />
              <Route path="/car-rental-yerevan-airport" element={<Suspense fallback={<PageLoader />}><CarRentalYerevanAirport /></Suspense>} />
              <Route path="/car-hire-yerevan-armenia" element={<Suspense fallback={<PageLoader />}><CarHireYerevan /></Suspense>} />
              <Route path="/rent-a-car-gyumri" element={<Suspense fallback={<PageLoader />}><RentACarGyumri /></Suspense>} />
              <Route path="/car-rental-istanbul-turkey" element={<Suspense fallback={<PageLoader />}><CarRentalIstanbul /></Suspense>} />
              <Route path="/4x4-rental-istanbul-turkey" element={<Suspense fallback={<PageLoader />}><CarRental4x4Istanbul /></Suspense>} />
              <Route path="/car-rental-istanbul-airport-turkey" element={<Suspense fallback={<PageLoader />}><CarRentalIstanbulAirport /></Suspense>} />
              <Route path="/trips" element={<Suspense fallback={<PageLoader />}><Trips /></Suspense>} />
              <Route path="/trips/georgia-expedition" element={<Suspense fallback={<PageLoader />}><GeorgiaExpedition /></Suspense>} />
              <Route path="/trips/georgia-family-trip" element={<Suspense fallback={<PageLoader />}><GeorgiaFamilyTrip /></Suspense>} />
              <Route path="/trips/georgia-armenia-expedition" element={<Suspense fallback={<PageLoader />}><GeorgiaArmeniaExpedition /></Suspense>} />
              <Route path="/blog" element={<Suspense fallback={<PageLoader />}><Blog /></Suspense>} />
              <Route path="/blog/:slug" element={<KeyedBlogPost />} />
              <Route path="/v/:slug" element={<Suspense fallback={<PageLoader />}><CarAccessGate /></Suspense>} />
              <Route path="/car/:slug" element={<KeyedCarDetail />} />
              <Route path="/checkout" element={<Suspense fallback={<PageLoader />}><Checkout /></Suspense>} />
              <Route path="/booking-confirmation" element={<Suspense fallback={<PageLoader />}><BookingConfirmation /></Suspense>} />
              <Route path="/privacy-policy" element={<Suspense fallback={<PageLoader />}><PrivacyPolicy /></Suspense>} />
              <Route path="/terms-of-service" element={<Suspense fallback={<PageLoader />}><TermsOfService /></Suspense>} />
              <Route path="/about" element={<Suspense fallback={<PageLoader />}><AboutUs /></Suspense>} />
              <Route path="/admin" element={<Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense>} />
              <Route path="/team-admin" element={<Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense>} />
              <Route path="/my-booking" element={<Suspense fallback={<PageLoader />}><CustomerPortal /></Suspense>} />
              {/* Non-canonical URL redirects to prevent soft 404s */}
              <Route path="/rent-a-car-batumi-georgia" element={<Navigate to="/car-rental-batumi-georgia" replace />} />
              <Route path="/car-rental-yerevan-airport-armenia" element={<Navigate to="/car-rental-yerevan-airport" replace />} />
              <Route path="/car-rental-tbilisi-to-yerevan-georgia" element={<Navigate to="/rent-car-tbilisi-to-yerevan" replace />} />
              <Route path="/car-rental-4x4-kutaisi" element={<Navigate to="/4x4-car-rental-kutaisi-georgia" replace />} />
              <Route path="/car-rental-tbilisi-yerevan" element={<Navigate to="/rent-car-tbilisi-to-yerevan" replace />} />
              <Route path="/car-rental-yerevan" element={<Navigate to="/car-rental-yerevan-armenia" replace />} />
              <Route path="/car-rental-4x4-tbilisi-georgia" element={<Navigate to="/4x4-car-rental-tbilisi-georgia" replace />} />
              <Route path="/car-rental-4x4-kutaisi-georgia" element={<Navigate to="/4x4-car-rental-kutaisi-georgia" replace />} />
              <Route path="/car-rental-kutaisi-georgia" element={<Navigate to="/rent-a-car-kutaisi-georgia" replace />} />
              {/* Car detail year-suffix redirects */}
              <Route path="/car/toyota-4runner-2024" element={<Navigate to="/car/toyota-4runner" replace />} />
              <Route path="/car/toyota-sienna-2022" element={<Navigate to="/car/toyota-sienna" replace />} />
              <Route path="/car/jeep-wrangler-2024" element={<Navigate to="/car/jeep-wrangler" replace />} />
              <Route path="/car/chevrolet-suburban-2022" element={<Navigate to="/car/chevrolet-suburban" replace />} />
              {/* Legacy car URLs (vehicles no longer in fleet) → closest available match */}
              <Route path="/car/lexus-rx-350" element={<Navigate to="/car/toyota-4runner" replace />} />
              <Route path="/car/lexus-rx-350/" element={<Navigate to="/car/toyota-4runner" replace />} />
              <Route path="/car/mercedes-benz-e-400" element={<Navigate to="/car/toyota-sienna" replace />} />
              <Route path="/car/mercedes-benz-e-400/" element={<Navigate to="/car/toyota-sienna" replace />} />
              <Route path="/car/toyota-sequoia" element={<Navigate to="/car/chevrolet-suburban" replace />} />
              <Route path="/car/toyota-sequoia/" element={<Navigate to="/car/chevrolet-suburban" replace />} />
              {/* Legacy WordPress URLs → relevant live pages */}
              <Route path="/drivers" element={<Navigate to="/about" replace />} />
              <Route path="/drivers/" element={<Navigate to="/about" replace />} />
              <Route path="/tours" element={<Navigate to="/trips" replace />} />
              <Route path="/tours/*" element={<Navigate to="/trips" replace />} />
              <Route path="/what-do-you-need-to-rent-a-car-in-tbilisi" element={<Navigate to="/best-car-rental-tbilisi-georgia" replace />} />
              <Route path="/what-do-you-need-to-rent-a-car-in-tbilisi/" element={<Navigate to="/best-car-rental-tbilisi-georgia" replace />} />
              <Route path="/how-to-rent-a-car-in-kutaisi-easily" element={<Navigate to="/rent-a-car-kutaisi-georgia" replace />} />
              <Route path="/how-to-rent-a-car-in-kutaisi-easily/" element={<Navigate to="/rent-a-car-kutaisi-georgia" replace />} />
              <Route path="/4x4-car-rental-batumi" element={<Navigate to="/car-rental-batumi-georgia" replace />} />
              <Route path="/4x4-car-rental-batumi/" element={<Navigate to="/car-rental-batumi-georgia" replace />} />
              <Route path="/hotel-in-tbilisi" element={<Navigate to="/blog" replace />} />
              <Route path="/hotel-in-tbilisi/*" element={<Navigate to="/blog" replace />} />
              {/* Legacy Russian (RU) language URLs — site no longer offers RU */}
              <Route path="/ru" element={<Navigate to="/" replace />} />
              <Route path="/ru/*" element={<Navigate to="/" replace />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </BookingProvider>
    </QueryClientProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
